import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthService } from './services/auth';
import { useAuthStore } from './store/authStore';
import Layout from './components/Layout';
import BuyerLandingPage from './pages/BuyerLandingPage';
import AgenciesLandingPage from './pages/AgenciesLandingPage';
import AgencySignupPage from './pages/AgencySignupPage';
import PromoteurSignupPage from './pages/PromoteurSignupPage';
import LoginPage from './pages/LoginPage';
import EnhancedLoginPage from './pages/EnhancedLoginPage';
import DashboardPage from './pages/DashboardPage';
import AgentDashboardPage from './pages/AgentDashboardPage';
import BuyerDashboardPage from './pages/BuyerDashboardPage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyCreatePage from './pages/PropertyCreatePage';
import PropertyViewPage from './pages/PropertyViewPage';
import PropertyEditPage from './pages/PropertyEditPage';
import ChantierViewPage from './pages/ChantierViewPage';
import ChantiersPage from './pages/ChantiersPage';
import ChantierCreatePage from './pages/ChantierCreatePage';
import ClientsPage from './pages/ClientsPage';
import ClientViewPage from './pages/ClientViewPage';
import ClientCreatePage from './pages/ClientCreatePage';
import ClientEditPage from './pages/ClientEditPage';
import MatchesPage from './pages/MatchesPage';
import MatchViewPage from './pages/MatchViewPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import UserManagementPage from './pages/UserManagementPage';
import PromoteurDashboardPage from './pages/PromoteurDashboardPage';
import OnboardingFlow from './pages/OnboardingFlow';
import NotFoundPage from './pages/NotFoundPage';
import ProtectedRoute from './components/ProtectedRoute';
import AIChatWidget from './components/AIChatWidget';
import TutorialStepper from './components/TutorialStepper';
import { UserRole } from './types';

const DashboardRedirect = () => {
  const { user } = useAuthStore();
  if (!user) return <Navigate to="/login" replace />;

  switch (user.role) {
    case UserRole.PROMOTEUR:
      return <PromoteurDashboardPage />;
    case UserRole.AGENT:
      return <AgentDashboardPage />;
    case UserRole.CLIENT:
      return <BuyerDashboardPage />;
    case UserRole.ADMIN:
      return <DashboardPage />; // Legacy/Admin dashboard
    default:
      return <BuyerDashboardPage />;
  }
};

function App() {
  const { user, setUser, clearUser } = useAuthStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = await AuthService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
        } else {
          clearUser();
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        clearUser();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [setUser, clearUser]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!user) {
    return (
      <>
        <Routes>
          <Route path="/" element={<BuyerLandingPage />} />
          <Route path="/crm" element={<AgenciesLandingPage />} />
          <Route path="/login" element={<EnhancedLoginPage />} />
          <Route path="/auth/verify" element={<EnhancedLoginPage />} />
          <Route path="/register/agency" element={<AgencySignupPage />} />
          <Route path="/register/promoteur" element={<PromoteurSignupPage />} />
          <Route path="/onboarding" element={<OnboardingFlow />} />
          <Route path="/chantiers" element={<ChantiersPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <AIChatWidget />
        <TutorialStepper />
      </>
    );
  }

  return (
    <Layout>
      <TutorialStepper />
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/crm" element={<AgenciesLandingPage />} />
        <Route path="/dashboard" element={<DashboardRedirect />} />
        <Route path="/onboarding" element={<OnboardingFlow />} />
        <Route path="/properties" element={<PropertiesPage />} />
        <Route path="/chantiers" element={<ChantiersPage />} />
        {/* Property routes - create must be before :id to avoid matching conflict */}
        <Route path="/properties/create" element={<PropertyCreatePage />} />
        <Route path="/chantiers/create" element={<ChantierCreatePage />} />
        <Route path="/properties/:id" element={<PropertyViewPage />} />
        <Route path="/chantiers/:id" element={<ChantierViewPage />} />
        <Route path="/properties/:id/edit" element={<PropertyEditPage />} />
        <Route path="/properties/edit/:id" element={<PropertyEditPage />} />
        <Route path="/clients" element={<ClientsPage />} />
        <Route path="/clients/create" element={<ClientCreatePage />} />
        <Route path="/clients/:id" element={<ClientViewPage />} />
        <Route path="/clients/:id/edit" element={<ClientEditPage />} />
        <Route path="/matches" element={<MatchesPage />} />
        <Route path="/matches/:id" element={<MatchViewPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route 
          path="/promoteur" 
          element={
            <ProtectedRoute requiredRole={UserRole.PROMOTEUR}>
              <PromoteurDashboardPage />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/users" 
          element={
            <ProtectedRoute requiredRole={UserRole.ADMIN}>
              <UserManagementPage />
            </ProtectedRoute>
          } 
        />
        <Route path="/login" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <AIChatWidget />
    </Layout>
  );
}

export default App;
